import { revokeKeyIds, setLicenseKey, licenseStatus, type LicenseStatus } from "@ortex-charts/core";
import { registerExtraIndicators } from "@ortex-charts/indicators-extra";
import { ensureStyles } from "@ortex-charts/ui";
import { scheduleIdle } from "@/lib/defer";
import { ORTEX_CHARTS_LICENSE_KEY } from "./license-key";

/**
 * One-time set-up for ORTEX Charts in this app, exactly as a licensed
 * customer does it: the key goes in once, the extra indicator package is
 * registered once, and the shell's stylesheet is injected once. Idempotent,
 * client only; every chart component calls it before creating a chart.
 */
let done: Promise<LicenseStatus> | null = null;

export const REVOCATION_LIST_URL = "https://cdn.ortexcharts.com/v1/revoked.json";

/**
 * Read the published revocation list and apply anything on it.
 *
 * This is what `setLicenseKey`'s own `revocationList` option does, moved out
 * here and off the chart's critical path. Passing the option makes
 * `setLicenseKey` await the fetch before it resolves, and since every chart
 * component gates its first draw on `ensureOrtexCharts()`, a cross-origin
 * round trip to a CDN - measured at 713ms on a cold stock page, and failing
 * CORS at the time, so the whole wait bought nothing - sat between the page
 * loading and the chart appearing.
 *
 * `revokeKeyIds` is the library's supported way to do this from the host, and
 * a key already installed drops to `revoked` the moment it is named, so a
 * revocation still lands - just after the chart has drawn rather than before.
 * Failure is silent by design: an unreachable list can only ever revoke, so
 * it must never take a working chart down.
 */
const applyRevocationList = (): void => {
    fetch(REVOCATION_LIST_URL, { credentials: "omit" })
        .then((res) => (res.ok ? res.json() : null))
        .then((body: unknown) => {
            const list = (body as { revoked?: unknown } | null)?.revoked;
            const ids = Array.isArray(list) ? list.filter((v): v is string => typeof v === "string") : [];
            if (ids.length) revokeKeyIds(...ids);
        })
        .catch(() => {
            /* unreachable list - nothing changes */
        });
};

export function ensureOrtexCharts(): Promise<LicenseStatus> {
    if (typeof window === "undefined") return Promise.resolve(licenseStatus());
    if (!done) {
        registerExtraIndicators();
        ensureStyles();
        // No `revocationList` here on purpose - see applyRevocationList above.
        done = setLicenseKey(ORTEX_CHARTS_LICENSE_KEY).then(() => licenseStatus());
        scheduleIdle(applyRevocationList);
    }
    return done;
}
